import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../../components/Sidebar/Sidebar';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faTimes, faUtensils, faSearch, faFilter, faEdit } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../Pagination/Pagination';
import { Howl } from 'howler';
import { buildApiUrl } from '../../config/api.config';

const deleteItem = new Howl({ src: ['/sounds/cancel.mp3'] });

const List = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({
    name: '',
    category: '',
    price: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchList = async () => {
    setIsLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;

      // Handle populated restaurantId object
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      if (!activeRestaurantId) {
        setList([]);
        setIsLoading(false);
        return;
      }

      const response = await axios.get(buildApiUrl(`/food/list?restaurantId=${activeRestaurantId}`));
      if (response.data.success) {
        setList(response.data.data);
      } else {
        toast.error("Error fetching the food list");
      }
    } catch (error) {
      toast.error("An error occurred while fetching the food list");
    } finally {
      setIsLoading(false);
    }
  };

  const confirmRemoveFood = async () => {
    if (!itemToDelete) return;
    
    try {
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;

      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      const response = await axios.post(buildApiUrl("/food/admin/remove"), { 
        id: itemToDelete._id,
        restaurantId: activeRestaurantId 
      });

      if (response.data.success) {
        toast.success(response.data.message);
        deleteItem.play();
        setShowDeleteModal(false);
        setItemToDelete(null);
        await fetchList();
      } else {
        toast.error(response.data.message || "Error removing the food item");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      const errorMsg = error.response?.data?.message || "An error occurred while removing the food item";
      toast.error(errorMsg);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      category: '',
      price: ''
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchList();
  }, []);

  const filteredList = list.filter((item) => {
    const matchesName = item.name.toLowerCase().includes(filters.name.toLowerCase());
    const matchesCategory = item.category.toLowerCase().includes(filters.category.toLowerCase());
    const matchesPrice = item.price.toString().includes(filters.price);
    return matchesName && matchesCategory && matchesPrice;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredList.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="admin-layout">
      <Sidebar />
      <main>
        <div className="tf-card" style={{ marginBottom: '2rem' }}>
          <header style={{ marginBottom: '2.5rem' }}>
            <h1 className="tf-heading-md tf-flex" style={{ gap: '0.75rem', marginBottom: '0.5rem' }}>
              <FontAwesomeIcon icon={faUtensils} style={{ color: 'var(--tf-primary)' }} />
              Menu Management
            </h1>
            <p style={{ color: 'var(--tf-text-muted)', fontSize: '0.95rem' }}>Maintain and organize your restaurant's culinary offerings</p>
          </header>

          <div style={{ padding: '2rem', background: 'rgba(255,255,255,0.02)', borderRadius: 'var(--tf-radius-lg)', border: '1px solid var(--tf-border)', marginBottom: '3rem' }}>
            <div className="tf-flex" style={{ gap: '0.75rem', marginBottom: '1.5rem', color: 'var(--tf-primary)' }}>
              <FontAwesomeIcon icon={faFilter} />
              <span style={{ fontWeight: '700', fontSize: '0.9rem', letterSpacing: '0.5px' }}>FILTER DISHES</span>
            </div>
            
            <div className="tf-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem', alignItems: 'flex-end' }}>
              <div className="tf-grid" style={{ gap: '0.6rem' }}>
                <p className="tf-text-accent" style={{ fontSize: '0.85rem', fontWeight: '600' }}>DISNAME</p>
                <div style={{ position: 'relative' }}>
                  <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--tf-text-muted)', fontSize: '0.8rem' }} />
                  <input
                    className="tf-input"
                    style={{ paddingLeft: '2.5rem' }}
                    type="text"
                    name="name"
                    placeholder="Search by name..."
                    value={filters.name}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>
              
              <div className="tf-grid" style={{ gap: '0.6rem' }}>
                <p className="tf-text-accent" style={{ fontSize: '0.85rem', fontWeight: '600' }}>CATEGORY</p>
                <input
                  className="tf-input"
                  type="text"
                  name="category"
                  placeholder="e.g. Starter"
                  value={filters.category}
                  onChange={handleFilterChange}
                />
              </div>
              
              <div className="tf-grid" style={{ gap: '0.6rem' }}>
                <p className="tf-text-accent" style={{ fontSize: '0.85rem', fontWeight: '600' }}>MAX PRICE</p>
                <input
                  className="tf-input"
                  type="text"
                  name="price"
                  placeholder="₹ 0.00"
                  value={filters.price}
                  onChange={handleFilterChange}
                />
              </div>
              
              <div>
                <button 
                  onClick={clearFilters}
                  className="tf-btn tf-btn-outline"
                  style={{ width: '100%', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', borderColor: 'var(--tf-border)' }}
                  disabled={!filters.name && !filters.category && !filters.price}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Reset
                </button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="tf-flex-center" style={{ padding: '6rem 2rem', flexDirection: 'column', gap: '1.5rem' }}>
              <div className="tf-spinner"></div>
              <p style={{ color: 'var(--tf-text-muted)', fontWeight: '500' }}>Fetching menu items...</p>
            </div>
          ) : (
            <div className="tf-list-container">
              <div className="tf-flex" style={{ marginBottom: '2rem', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid rgba(255,255,255,0.05)', paddingBottom: '1rem' }}>
                <h2 className="tf-heading-sm" style={{ fontSize: '1.3rem', fontWeight: '700' }}>Dishes List</h2>
                <div className="tf-flex" style={{ gap: '1rem', background: 'rgba(255,255,255,0.03)', padding: '0.4rem 1rem', borderRadius: 'var(--tf-radius-md)' }}>
                  <span style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem', fontWeight: '600' }}>DISPLAY:</span>
                  <select
                    className="tf-input"
                    style={{ width: 'auto', padding: '0.2rem 0.5rem', border: 'none', background: 'transparent', fontSize: '0.85rem', fontWeight: '700', color: 'var(--tf-primary)' }}
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  >
                    {[5, 10, 20, 50].map(num => (
                      <option key={num} value={num}>{num} Per Page</option>
                    ))}
                  </select>
                </div>
              </div>

              {currentItems.length === 0 ? (
                <div className="tf-empty-state" style={{ background: 'rgba(255,255,255,0.01)', borderRadius: 'var(--tf-radius-lg)' }}>
                   <FontAwesomeIcon icon={faUtensils} size="3x" style={{ marginBottom: '1.5rem', opacity: 0.1 }} />
                  <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>
                    {Object.values(filters).some(Boolean) ? 
                    'No dishes match your current filters' : 
                    'Your menu is currently empty'}
                  </p>
                  <button onClick={clearFilters} className="tf-btn tf-btn-primary" style={{ marginTop: '1.5rem', padding: '0.6rem 2rem' }}>Clear All Filters</button>
                </div>
              ) : (
                <>
                  <div className="tf-list-header" style={{ gridTemplateColumns: '100px 2fr 1.5fr 1fr 100px', gap: '1.5rem', padding: '1.25rem 2rem' }}>
                    <span>DISH</span>
                    <span>NAME & DESCRIPTION</span>
                    <span>CATEGORY</span>
                    <span>PRICE</span>
                    <span style={{ textAlign: 'right' }}>ACTION</span>
                  </div>

                  <div className="tf-grid" style={{ gap: '0' }}>
                    {currentItems.map((item) => (
                      <div key={item._id} className="tf-list-item" style={{ gridTemplateColumns: '100px 2fr 1.5fr 1fr 100px', gap: '1.5rem', padding: '1.5rem 2rem' }}>
                        <div style={{ position: 'relative' }}>
                          <img
                            src={buildApiUrl(`/uploads/${item.image}`)}
                            alt={item.name}
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="70" height="70" viewBox="0 0 70 70"%3E%3Crect width="70" height="70" fill="%23333"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23666" font-size="24"%3E🍽️%3C/text%3E%3C/svg%3E';
                            }}
                            style={{ width: '70px', height: '70px', borderRadius: '12px', objectFit: 'cover', border: '2px solid var(--tf-border)', boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}
                          />
                        </div>
                        <div style={{ overflow: 'hidden' }}>
                          <div style={{ fontWeight: '700', color: 'var(--tf-text-white)', fontSize: '1.05rem', marginBottom: '0.25rem' }}>{item.name}</div>
                          <div style={{ color: 'var(--tf-text-muted)', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.description || 'No description provided.'}</div>
                        </div>
                         <div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <span style={{ background: 'rgba(255,255,255,0.05)', padding: '0.3rem 0.8rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600', color: 'var(--tf-text-muted)', width: 'fit-content' }}>
                              {item.category}
                            </span>
                            {item.recipe && item.recipe.length > 0 && (
                              <span style={{ background: 'rgba(0, 209, 178, 0.1)', padding: '0.3rem 0.8rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700', color: '#00d1b2', width: 'fit-content', border: '1px solid rgba(0, 209, 178, 0.2)' }}>
                                <i className="fa-solid fa-flask" style={{ marginRight: '5px' }}></i> {item.recipe.length} Ingredient{item.recipe.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ fontWeight: '700', color: 'var(--tf-primary)', fontSize: '1.1rem' }}>₹{item.price.toFixed(2)}</div>
                        <div style={{ textAlign: 'right', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button 
                            onClick={() => navigate(`/admin/edit-food/${item._id}`)}
                            className="tf-btn"
                            style={{ width: '40px', height: '40px', padding: '0', borderRadius: '50%', backgroundColor: 'rgba(0, 209, 178, 0.1)', color: 'var(--tf-success)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Edit Dish"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button 
                            onClick={() => { setItemToDelete(item); setShowDeleteModal(true); }}
                            className="tf-btn"
                            style={{ width: '40px', height: '40px', padding: '0', borderRadius: '50%', backgroundColor: 'rgba(255, 56, 96, 0.1)', color: 'var(--tf-error)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Delete Dish"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              
              <div style={{ marginTop: '3rem', borderTop: '1px solid var(--tf-border)', paddingTop: '2rem' }}>
                <Pagination
                  totalItems={filteredList.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  paginate={paginate}
                />
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="tf-modal-overlay">
            <div className="tf-modal" style={{ maxWidth: '400px', borderRadius: '16px', overflow: 'hidden' }}>
              <div className="tf-modal-body" style={{ textAlign: 'center', padding: '2.5rem 2rem 1.5rem 2rem' }}>
                <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: 'rgba(255, 56, 96, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto', border: '1px solid rgba(255, 56, 96, 0.2)' }}>
                  <FontAwesomeIcon icon={faTrashAlt} size="2x" style={{ color: 'var(--tf-error)' }} />
                </div>
                <h2 className="tf-heading-sm" style={{ marginBottom: '0.8rem', color: 'var(--tf-text-white)', fontSize: '1.4rem', fontWeight: '700' }}>Delete Food Item?</h2>
                <p style={{ color: 'var(--tf-text-muted)', marginBottom: '1.5rem', lineHeight: '1.6', fontSize: '0.95rem' }}>
                  Are you sure you want to permanently delete <br/>
                  <strong style={{ color: 'var(--tf-text-white)', fontSize: '1.1rem', display: 'inline-block', marginTop: '0.5rem' }}>{itemToDelete?.name}</strong>?
                </p>
                
                <div style={{ background: 'rgba(255, 56, 96, 0.05)', border: '1px dashed rgba(255, 56, 96, 0.3)', padding: '0.8rem', borderRadius: '8px' }}>
                  <p style={{ color: 'var(--tf-error)', fontSize: '0.85rem', fontWeight: '600', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>This action cannot be undone</p>
                </div>
              </div>
              <div className="tf-modal-footer" style={{ display: 'flex', gap: '1rem', padding: '1.5rem 2rem', background: 'rgba(0, 0, 0, 0.15)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <button className="tf-btn tf-btn-outline" style={{ flex: 1, padding: '0.8rem' }} onClick={() => { setShowDeleteModal(false); setItemToDelete(null); }}>
                  Cancel
                </button>
                <button className="tf-btn tf-btn-primary" style={{ flex: 1, padding: '0.8rem', background: 'var(--tf-error)', borderColor: 'var(--tf-error)', boxShadow: '0 4px 12px rgba(255, 56, 96, 0.3)' }} onClick={confirmRemoveFood}>
                  Delete Item
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default List;